/**
 * The "deliberate switch" M1-29's AC calls for: a static JSON file, polled on
 * a standing interval, that planned work can flip on and off without a
 * redeploy. Defaults to a same-origin file this app builds and deploys
 * itself (`public/maintenance-flag.json`, always `{"maintenance": false}` as
 * shipped) — flipping it in place still needs SOME way to overwrite one file
 * without redeploying the SPA; `VITE_MAINTENANCE_FLAG_URL` points this at
 * wherever that turns out to be (a blob container, a tiny separate static
 * site — whatever can be overwritten in seconds, independent of this app's
 * own deploy pipeline).
 *
 * `index.html`'s own inline bootstrap script checks the SAME default path
 * before the React bundle even loads, so a flagged maintenance window does
 * not depend on the JS bundle — see that file's own comment.
 */
export interface MaintenanceFlag {
  maintenance: boolean;
  message?: string;
}

const FLAG_URL = import.meta.env.VITE_MAINTENANCE_FLAG_URL || '/maintenance-flag.json';

const OFF: MaintenanceFlag = { maintenance: false };

export async function fetchMaintenanceFlag(): Promise<MaintenanceFlag> {
  try {
    const response = await fetch(FLAG_URL, { cache: 'no-store', credentials: 'omit' });
    if (!response.ok) {
      return OFF;
    }
    const data: unknown = await response.json();
    if (typeof data !== 'object' || data === null || !('maintenance' in data)) {
      return OFF;
    }
    const { maintenance, message } = data as { maintenance: unknown; message?: unknown };
    return {
      maintenance: maintenance === true,
      message: typeof message === 'string' ? message : undefined,
    };
  } catch {
    // No flag file, a network blip, invalid JSON — all read the same as "not
    // flagged". The file's ABSENCE is the normal case for anyone who has not
    // set up an override location, and a broken fetch here must never itself
    // manufacture an outage.
    return OFF;
  }
}
